/**
 * StorageQuotaMonitor — Aviso proactivo de almacenamiento local.
 *
 * QUÉ HACE:
 *  - Chequea el uso de localStorage al montar, periódicamente y al volver a
 *    enfocar la app. Muestra un toast una sola vez por nivel de riesgo
 *    (Aviso / Alerta / Crítico) y, en estado crítico, un botón flotante
 *    persistente para abrir Ajustes → Almacenamiento.
 *  - No bloquea ventas: solo informa. Si la escritura falla, el error honesto
 *    lo muestra el flujo que falló (StorageQuotaError).
 * QUÉ INVOCA: getStorageUsageReport, STORAGE_SETTINGS_OPEN_EVENT.
 * QUÉ NO DEBE HACER: Borrar datos, abrir modales por sí solo ni bloquear la UI.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { HardDrive, X } from "lucide-react";
import {
  LOCAL_STORAGE_REF_CAP_BYTES,
  STORAGE_SETTINGS_OPEN_EVENT,
  formatBytes,
  getStorageUsageReport,
  type StorageUsageLevel,
} from "@/lib/persistence/storageUsage";
import { STORAGE_QUOTA_EVENT } from "@/mocks/helpers/storage";

const POLL_INTERVAL_MS = 60_000;

function levelRank(level: StorageUsageLevel): number {
  if (level === "critical") return 3;
  if (level === "alert") return 2;
  if (level === "warning") return 1;
  return 0;
}

export function StorageQuotaMonitor() {
  const [critical, setCritical] = useState(false);
  const [percent, setPercent] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  /** Niveles ya notificados en esta sesión (una sola vez cada uno). */
  const notifiedRef = useRef<Set<StorageUsageLevel>>(new Set());

  const check = useCallback(() => {
    const report = getStorageUsageReport();
    setPercent(report.percentUsed);
    setCritical(report.level === "critical");

    if (report.level === "ok") return;
    if (notifiedRef.current.has(report.level)) return;

    notifiedRef.current.add(report.level);
    const remaining = LOCAL_STORAGE_REF_CAP_BYTES - report.totalBytes;
    const remLabel = formatBytes(Math.max(0, remaining));
    const message = (() => {
      if (levelRank(report.level) >= 3) {
        return `Almacenamiento al ${report.percentUsed}% (queda ${remLabel}): puede dejar de guardar. Descarga tu respaldo y libera espacio en Ajustes → Almacenamiento.`;
      }
      if (levelRank(report.level) >= 2) {
        return `Almacenamiento al ${report.percentUsed}% (queda ${remLabel}): poco espacio. Descarga tu respaldo y libera espacio en Ajustes → Almacenamiento.`;
      }
      return `Almacenamiento al ${report.percentUsed}% (queda ${remLabel}). Revisa cuánto espacio te queda en Ajustes → Almacenamiento.`;
    })();
    toast(message, {
      duration: 9000,
      id: `storage-level-${report.level}`,
    });
  }, []);

  useEffect(() => {
    check();
    const interval = window.setInterval(check, POLL_INTERVAL_MS);
    const onFocus = () => check();
    const onQuota = () => check();
    window.addEventListener("focus", onFocus);
    window.addEventListener(STORAGE_QUOTA_EVENT, onQuota);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(STORAGE_QUOTA_EVENT, onQuota);
    };
  }, [check]);

  const openSettings = () => {
    window.dispatchEvent(
      new CustomEvent(STORAGE_SETTINGS_OPEN_EVENT, {
        detail: { view: "storage" },
      }),
    );
  };

  if (!critical || dismissed) return null;

  return (
    <div
      className="fixed bottom-20 right-3 z-[80] flex items-center gap-2 rounded-full border border-red-200 bg-red-50 py-1.5 pl-3 pr-1.5 shadow-lg dark:border-red-900/60 dark:bg-red-950/95 sm:bottom-6"
      role="status"
      aria-live="polite"
    >
      <HardDrive className="h-4 w-4 shrink-0 text-red-600 dark:text-red-300" />
      <p className="text-[11px] font-bold leading-tight text-red-800 dark:text-red-100">
        Memoria al {Math.round(percent)}%
      </p>
      <button
        type="button"
        onClick={openSettings}
        className="rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-bold text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-400"
      >
        Liberar espacio
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Ocultar aviso de memoria"
        className="rounded-full p-1 text-red-400 hover:bg-red-100 dark:text-red-200 dark:hover:bg-red-900/50"
      >
        <X size={12} />
      </button>
    </div>
  );
}

export default StorageQuotaMonitor;
